
  import { Component, Watch, Vue, Prop }          from 'vue-property-decorator';
  import { Action, Getter }                       from 'vuex-class';
  import { EventBus,
           HtmlFormatHelper,
           ResponsiveUiHelper,
           ConfigService,
           ConfigThemeService,
           SessionEntity,
           UserMessageTypeEnum,
           WidgetCommandEnum }                    from '../..';
  import   SettingsComponent                      from '../settings/SettingsComponent.vue';


  @Component({
    name: 'TyperComponent',
    components: {
      SettingsComponent
    }
  })
  export default class TyperComponent extends Vue {
    @Prop() displayExperimentalMode!: boolean;
    @Prop() nbMessages!: number;

    @Action('sendMessage', { namespace: 'messages' }) sendMessage: any;
    @Action('retrieveSuggestedQuestions', { namespace: 'conversationSession'}) retrieveSuggestedQuestions: any;

    @Getter('isEmbeddedMode', { namespace: 'conversationSession' }) isEmbeddedMode!: boolean | undefined;
    @Getter('isRequestInProgress', { namespace: 'messages' }) isRequestInProgress!: boolean;
    @Getter('suggestionsList', { namespace: 'conversationSession' }) suggestionsList!: string[] | undefined;
    @Getter('conversationSessionInfo', { namespace: 'conversationSession' }) conversationSessionInfo!: SessionEntity | undefined;
  
    suggestionsEngine: any = undefined;
    suggestionsToShow: string[] | undefined = [];
    selectedSuggestion: string = '';
    suggestionIsSelected: boolean = false;
    typedUserInput: string = '';
    isOffsetActive: boolean = true;
    showSuggestionsList: boolean = false;
    isFullscreenDisplay: boolean = false;
    displayError: boolean = false;
    displayErrorOnHover : boolean = false;
    isActionInitiated: boolean = false;
    maxCharacterLimitReached: boolean = false;
    messageError: string = "";
    loseFocusSaveInput: string = "";

    /** ******************************************************************** */
    /**                      Get styles                                      */
    /** ******************************************************************** */

    get typerPlaceholder() {
      return ConfigService.getMessageTyperPlaceholder();
    }

    get inputStyleName() {
      return (this.isFullscreenDisplay || !this.isEmbeddedMode) ? 'solo' : 'rounded';
    }

    get typerStyles() {
      return (this.isEmbeddedMode && !this.isFullscreenDisplay) ? ConfigThemeService.getTyperStyles(this.$vuetify) : {};
    }

    get typerSendBtnStyles() {
      return {  marginRight: '16px',
                marginTop: 'auto !important',
                marginBottom: 'auto !important' };
    }

    get typerHeight() {
      return ConfigThemeService.getTyperHeight();
    }

    get typerSendIcon() {
      return ConfigThemeService.getSendIcon();
    }

    get typerColor() {
      return ConfigThemeService.getTyperColor(this.$vuetify);
    }

    get typerIconsColor() {
      return ConfigThemeService.getTyperIconsColor(this.$vuetify);
    }

    get typerSuggestionsStyles() {
      return { color: ConfigThemeService.getTyperSuggestionsColor(this.$vuetify) };
    }

    get loadingAnimationColor() {
      return ConfigThemeService.getLoadingAnimationColor(this.$vuetify);
    }

    get experimentalModeTxt() {
      return ConfigService.getExperimentalModeTexte();
    }

    get limitCharacterErrorTxt() {
      return ConfigService.getLimitCharacterErrorText();
    }
    get limitCharacterErrorPasteTxt() {
      return ConfigService.getLimitCharacterErrorPaste();
    }

    get messageCharactereLimit(){
      return ConfigService.getLimitMessageCharactere();
    }

    get textFontSize() {
      if(ConfigService.getDefaultPanelTextFontSize() !== undefined){
        return { fontSize : ConfigService.getDefaultPanelTextFontSize()+'px !important'};
      } else{ // if user didn't define font size => use breakpoints of ChatView.vue
        return {}; 
      }
    }

    get textProgressBarLoading() {
      return ConfigService.getHiddenTextProgressBarLoading();
    }

    get textProgressBarLoaded() {
      return ConfigService.getHiddenTextProgressBarLoaded();
    }
    
    /** ********************************************************************* */
    /**                           TITTLE FOR BUTTONS                          */
    /** ********************************************************************* */

    get sendButtonTitle() {
      return ConfigService.getSendButtonTitle();
    }

    /** ******************************************************************** */
    /**                     SELECTED ELEMENT MANAGEMENT                      */
    /** ******************************************************************** */

    // Id of selected element
    selectedSuggestionIndex: number | null = null;

    // Build ID for list option
    get getAriaActivedescendant(): string {
      return this.selectedSuggestionIndex !== null ? `option-${this.selectedSuggestionIndex}` : '';
    }

    get activeAutoFocus() {
      return !this.isFullscreenDisplay ;
    }

    // When the selected suggestion changes, update the index
    @Watch('selectedSuggestion', { immediate: true, deep: true })
    onSelectedSuggestionChanged(newValue: string, oldValue: string) {
      if (this.suggestionsToShow) {
        const index = this.suggestionsToShow.findIndex(suggestion => suggestion === newValue);
        this.selectedSuggestionIndex = index >= 0 ? index : null;
      }
    }

    /** ******************************************************************** */
    /**                      LIFECYCLE HOOKS FUNCTIONS                       */
    /** ******************************************************************** */

    created () {
      if(this.suggestionsList === undefined) {
        this.retrieveSuggestedQuestions();
      }
      this.showSuggestionsList = true;

      EventBus.$on('ON_RESIZE_SCREEN_MODE', (isFullscreen: boolean) => {
        this.isFullscreenDisplay = isFullscreen;
      });
      this.isFullscreenDisplay = ResponsiveUiHelper.isFullscreenDisplay(this.$vuetify);
    }

    updated() {
      // Detection of the moment when the list of proposals of the autocomplete is updated
      // in order to move the list if necessary
      this.$nextTick(() => {
        const autoCompleteContentElems : NodeListOf<HTMLElement> = document.querySelectorAll('.v-autocomplete__content');
        const autoCompleteContentElem : HTMLElement | null = autoCompleteContentElems[0];
        const typerElement : HTMLElement | null = document.getElementById('str-bot-msg-typer-container-id');

        if(autoCompleteContentElem && typerElement) {
          // setTimeout. Trick to ensure that the rendering of the list in the autocompletion is finished
          setTimeout(() => { this.onResizePositionAutoComplete(typerElement, autoCompleteContentElem); }, 100);
        }
      });
    }

    mounted() {
      // Make the send button focusable
      this.setAccessibility(this.$el.parentNode?.querySelector('.mdi-send'), 3, ConfigService.getSendButtonTitle());

      // Listen to the keydown event
      document.addEventListener('keydown', this.handleKeydown);
    }

    beforeDestroy() {
      // Remove the keydown event listener
      document.removeEventListener('keydown', this.handleKeydown);
    }

    destroyed () {
      EventBus.$off('ON_RESIZE_SCREEN_MODE');
    }

    handleKeydown(event: KeyboardEvent) {
      if (event.key === 'ArrowUp') {
        this.handleArrowUp();
      }else if (event.key === 'ArrowDown') {
        this.handleArrowDown();
      }
    }

    /** ******************************************************************** */
    /**                      Utility functions                               */
    /** ******************************************************************** */
  
    customFilter (item: object, queryText: string, itemText: string) {
      let text = itemText.toLowerCase()
      let searchText = queryText.toLowerCase()
      //eslint-disable-next-line
      const regex = /[-'’«»“”"\(\)\[\]\{\}\/\\?!:`,;\._|]/gi;
      text = text.replace(regex, '');
      searchText = searchText.replace(regex, '');
      return text.indexOf(searchText) > -1
    }

    // In rare cases the bot doesn't deliver an answer so there's no need to wait something
    waitForAnswer (msg: string) {
      return (msg === WidgetCommandEnum.RELOAD ? false : true);
    }

    /** ******************************************************************** */
    /**                      Action management                               */
    /** ******************************************************************** */
    // In mobile mode, we have to define the position of the autocomplete list according to the bottom
    onResizePositionAutoComplete (typerElement: HTMLElement | null, autoCompleteElement: HTMLElement | null) {
      if (typerElement  && typerElement.getBoundingClientRect() 
                        && autoCompleteElement 
                        && autoCompleteElement.getBoundingClientRect() 
                        && typerElement.getBoundingClientRect().top > autoCompleteElement.getBoundingClientRect().bottom 
                        && autoCompleteElement.getBoundingClientRect().height > 0) {

        autoCompleteElement.style.top = '';
        autoCompleteElement.style.bottom = (typerElement.getBoundingClientRect().height -1)+"px";

      } else if (autoCompleteElement && autoCompleteElement.style && autoCompleteElement.style.bottom) {
        autoCompleteElement.style.top = '';
      }
    }

    @Watch('selectedSuggestion') 
    onSuggestionSelected() {
      // on click in one suggestion element, the selectedSuggestion change
      // then the request could be sent
      this.sendUserInput();
    }

    @Watch('typedUserInput')
    onInputChanged() {
      this.suggestionIsSelected = false;
      if (this.typedUserInput && this.typedUserInput.length >= 1) {
        // activate suggestions list
        this.suggestionsToShow = this.suggestionsList;

        // Make the send button focusable
        this.waitForElement('.mdi-close', (closeButton: HTMLElement) => { this.setAccessibility(closeButton, 3, ConfigService.getRemoveButtonTitle()); });
        this.waitForElement('.mdi-send', (sendButton: HTMLElement) => { this.setAccessibility(sendButton, 3, ConfigService.getSendButtonTitle()); });
      } else {
        if (this.loseFocusSaveInput) {
          // Just lose focus with "tab" : retain the last input
          this.$nextTick(() => {
            this.$el.parentNode?.querySelector('.mdi-close')?.setAttribute('style', `color: ${this.typerIconsColor}; caret-color: ${this.typerIconsColor};`);
            this.$el.parentNode?.querySelector('.mdi-send')?.setAttribute('style', `color: ${this.typerIconsColor}; caret-color: ${this.typerIconsColor};`);
            this.typedUserInput = this.loseFocusSaveInput ?? '';
            this.loseFocusSaveInput = "";
          });
        } else {
          // Make the send button not focusable
          this.waitForElement('.mdi-close', (closeButton: HTMLElement) => { this.setAccessibility(closeButton, -1, ConfigService.getRemoveButtonTitle()); });
          this.waitForElement('.mdi-send', (sendButton: HTMLElement) => { this.setAccessibility(sendButton, -1, ConfigService.getSendButtonTitle()); });
        }
      }
    }

    @Watch('displayExperimentalMode')
    onExperimentalModeChange(){
      if(this.displayExperimentalMode){
        this.showErrorOnHover(this.experimentalModeTxt)
      }
      else {
        this.hideError();
      }
    }

    handleArrowUp() {
      // if suggestions list is empty, we can scroll up
      if(!this.suggestionsToShow || this.suggestionsToShow.length == 0){
        const messagesContainer = document.getElementById('chatbox-msg-list');
        if(messagesContainer){
          messagesContainer.scrollTop -= 25;
        }
      }else{
        this.suggestionIsSelected = true;
      }
      
    }

    handleArrowDown() {
      // If the suggestions list is empty, we can scroll down
      if(!this.suggestionsToShow || this.suggestionsToShow.length == 0){
        const messagesContainer = document.getElementById('chatbox-msg-list');
        if(messagesContainer){
          messagesContainer.scrollTop += 25;
        }
      }else{
        this.suggestionIsSelected = true;
      }
    }

    handleEnterKey() {
      if(!this.suggestionIsSelected){
        this.sendUserInput();
      }
    }

    handleTabKey(event: KeyboardEvent) {
      // If the user presses the tab key, the input field will lose focus
      // VAutocomplete component will then clear the input field
      // Remember the input value so that we can restore it in the next tick
      if (this.typedUserInput?.length > 0) {
        this.loseFocusSaveInput = this.typedUserInput;
      }
    }

    sendUserInput() {
      this.isActionInitiated = true;
      let textContextToSend: string = this.selectedSuggestion ? "##suggestion##" + this.selectedSuggestion : this.typedUserInput;
      let msgTextToSend: string = this.selectedSuggestion ? this.selectedSuggestion : this.typedUserInput;

      if (msgTextToSend) {

        // Check input length
        if(this.typedUserInput && this.typedUserInput.length > this.messageCharactereLimit.valueOf()){
          // If the character limit is exceeded, we do nothing
            return;
        }

        // verify that the message has no HTML tags or any chain that represents a risk
        msgTextToSend = HtmlFormatHelper.cleanHtml(msgTextToSend);
        msgTextToSend = msgTextToSend.trim();

        if (msgTextToSend.length > 0 && msgTextToSend.replace(/\s/g, '').length > 0) {
          EventBus.$emit('ON_MESSAGE_SENT');
          this.sendMessage({  sessionInfo: this.conversationSessionInfo, 
                              msgText: msgTextToSend,
                              textContent: textContextToSend,
                              updateLoadingState: true, 
                              waitForAnswer: this.waitForAnswer(msgTextToSend), 
                              msgType: UserMessageTypeEnum.USER_MSG });
        }
      }

      // initialize form state
      this.suggestionsToShow = undefined;
      this.showSuggestionsList = false;
      this.selectedSuggestion = '';
      this.typedUserInput = '';
    }

    closeFeedbackPanel() {
      // The feedback banner remains "open" until the text input field is activated
      EventBus.$emit('ON_CLOSE_FEEDBACK_PANEL');
      // initialize suggestions to show list state
      this.suggestionsToShow = undefined;
    }

    // Listen to the changes in the typer
    listenChangeInput(){
      let msgTextToSend: string = this.typedUserInput;
      // If the limit of 300 characters is exceeded, the typer is blocked and an error message is displayed
      if(msgTextToSend && msgTextToSend.length > this.messageCharactereLimit.valueOf() && !this.displayError){
        this.showError(this.limitCharacterErrorTxt);
        this.typedUserInput = msgTextToSend.slice(0, +this.messageCharactereLimit);
        this.maxCharacterLimitReached = true;
      } else if (msgTextToSend && msgTextToSend.length < this.messageCharactereLimit && this.displayError) {
        this.maxCharacterLimitReached = false;
        this.hideError();
      }
    }

    setAccessibility(element: HTMLElement | null | undefined, index: number, text: string) {
      if (element) {
        let srOnlySpan = element.querySelector('.sr-only');
        if (!srOnlySpan) {
          // create span if not exists
          srOnlySpan = document.createElement('span');
          srOnlySpan.classList.add('sr-only');
          srOnlySpan.textContent = text;
          element.appendChild(srOnlySpan);
        }

        // set attributes
        element?.setAttribute('title', text)
        element?.setAttribute('tabindex', index.toString());
        }
    }

    waitForElement(selector: string, callback: Function) {
      const attempts = 10;
      let attemptsMade = 0;

      const interval = setInterval(() => {
        const element = this.$el.parentNode?.querySelector(selector);
        if (element) {
          clearInterval(interval);
          callback(element);
        } else if (attemptsMade >= attempts) {
          clearInterval(interval);
        }
        attemptsMade++;
      }, 100); // Try every 100 ms
    }

    clearContent(){
      this.hideError();
    }

    showError(message : string){
      this.displayErrorOnHover = true;
      this.displayError = true;
      this.messageError = message;
    }

    showErrorOnHover(message : string){
      this.displayErrorOnHover = true;
      this.displayError = false;
      this.messageError = message;
    }

    hideError(){
      this.displayErrorOnHover = false;
      this.displayError = false;
      this.messageError = "";
    }
    handleKeyDown(event: KeyboardEvent) {
      const allowedKeys = [
        'Backspace', 
        'Delete', 
        'ArrowLeft', 
        'ArrowRight', 
        'Tab', 
        'Escape', 
        'Enter',
        'Control', 
        'Meta',  // for Command on Mac
        'Shift', 
        'Home', 
        'End',
      ];
      const isCtrlCommand = event.ctrlKey || event.metaKey;
      if (this.maxCharacterLimitReached){
          if (!allowedKeys.includes(event.key)) {
          // Allow any command key combinations (e.g., Ctrl+anything or Cmd+anything)
          if (!isCtrlCommand) {
            event.preventDefault(); // Block key if it's not part of a command
          }
        }
      }
    }
    handlePaste(event: ClipboardEvent) {
      let pasteText = "";

      // Explicit null check
      if (event.clipboardData !== null) {
        pasteText = event.clipboardData.getData('text'); // TypeScript will now recognize clipboardData as non-null
      }
      const currentLength = this.typedUserInput ? this.typedUserInput.length : 0;
      const newTextLength = currentLength + pasteText.length;

      if (newTextLength > this.messageCharactereLimit) {
        event.preventDefault(); // Prevent the paste action
        this.showError(this.limitCharacterErrorPasteTxt);
      } else {
        this.hideError();
      }
    }
    

}
